/* Sdílená brandovaná hlavička/pata pro tiskové výstupy (window.open HTML prints).
   Sjednocuje vzhled napříč kalkulačkami: logo/jméno poradce + kontakt + brand barva.
   Vstup: advisor profil (z useProfile() → /api/profile/me). Vrací HTML string. */

/* Překlad UI textů do pěti jazyků; `lang` sem chodí parametrem,
   takže se používá varianta s výslovným jazykem (lib/prekladyUI.js). */
import { TLj } from "@/lib/prekladyUI";

const esc = (s) => String(s == null ? "" : s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/* Brand barva jde do `style="…"` tiskových HTML (nové okno se stejným původem
 * jako appka). Backend hlídá jen délku (max 20 znaků), ne tvar — hodnota
 * s uvozovkou by z atributu utekla. Propustí se jen tvar, který barva opravdu
 * má (hex nebo název barvy, stejně jako barvaBezpecne v tiskDotazniku.js);
 * cokoli jiného spadne na výchozí odstín. (5. 10. 2026, dráha vzhled) */
const BARVA_OK = /^(#[0-9a-fA-F]{3,8}|[a-zA-Z]{3,20})$/;
export function brandColorOf(advisor) {
  const c = advisor && typeof advisor.brand_color === "string" ? advisor.brand_color.trim() : "";
  return BARVA_OK.test(c) ? c : "#F47920";
}

/* Šedá pro drobné písmo hlavičky a paty tisku: dřív #888 (3,54 : 1) a #999
 * (2,85 : 1) na bílém papíře, 9–11 px. #666 = 5,74 : 1. */
const SEDA_TISK = "#666";
const INKOUST_TISK = "#1a1a1a";

/* ── ČITELNÁ BRAND BARVA (5. 10. 2026, velká kontrola V1-06) ─────────────────
 * Brand barva poradce se kreslí jako TEXT i jako VÝPLŇ pod bílým písmem.
 * Výchozí oranžová #F47920 má bílou 2,76 : 1 a na papíře #fafaf4 jako text
 * 2,64 : 1; terakota #c4673a bílou 3,93 a ani tmavé písmo #1d1b16 (4,38)
 * nestačí. Prostá volba bílá/tmavá tedy nestačí — proto tyto pomocníky.
 *
 * ⚠️ Na PAPÍROVÝCH stránkách (Plný plán, 1 strana, veřejná modelace) předávej
 * HEX inkoust stránky, ne var(--ink): v Midnight je --ink světlé #F2F4F7 a text
 * by na bílém papíře vybledl. */
const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const jeHex = (c) => typeof c === "string" && HEX.test(c.trim());
function hexNaRgb(h) {
  let x = h.trim().slice(1);
  if (x.length === 3) x = x.split("").map((c) => c + c).join("");
  return [0, 2, 4].map((i) => parseInt(x.slice(i, i + 2), 16));
}
const naHex = (rgb) => "#" + rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");
const doLin = (v) => { const c = v / 255; return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4; };
const zLin = (v) => 255 * (v <= 0.0031308 ? 12.92 * v : 1.055 * Math.max(0, v) ** (1 / 2.4) - 0.055);

/** WCAG 2.x kontrast dvou hex barev (1–21). Pro ne-hex vrací NaN. */
export function kontrastHex(a, b) {
  if (!jeHex(a) || !jeHex(b)) return NaN;
  const jas = (h) => { const [r, g, bl] = hexNaRgb(h).map(doLin); return 0.2126 * r + 0.7152 * g + 0.0722 * bl; };
  const x = jas(a), y = jas(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

function doOklab(h) {
  const [r, g, b] = hexNaRgb(h).map(doLin);
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}
function zOklab([L, a, b]) {
  const l = (L + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m = (L - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s = (L - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return naHex([4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s, -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s, -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s].map(zLin));
}
/** Míchání dvou hex barev v OKLab (jako CSS color-mix(in oklab, a p%, b)). */
export function mixOklab(a, b, podilA) {
  const x = doOklab(a), y = doOklab(b), p = Math.max(0, Math.min(100, podilA)) / 100;
  return zOklab(x.map((v, i) => v * p + y[i] * (1 - p)));
}

/** Brand barva jako TEXT (≥ 4,5 : 1).
 *  • papír (`ink` je hex): vrátí HEX s co největším podílem brand barvy, který
 *    na všech předaných plochách drží 4,5 : 1 (tmavá značka zůstane, jak je),
 *  • appka (`ink` je var(--…)): color-mix 30 % brand / 70 % inkoustu motivu —
 *    spočítáno ≥ 4,57 : 1 v šesti motivech i pro žlutou a azurovou značku. */
export function brandText(brand, ink = "var(--ink)", plochy = ["#ffffff"]) {
  const b = jeHex(brand) ? brand.trim() : "#F47920";
  if (!jeHex(ink)) return `color-mix(in oklab, ${b} 30%, ${ink})`;
  const hexPlochy = (plochy || []).filter(jeHex);
  const drzi = (c) => hexPlochy.every((s) => kontrastHex(c, s) >= 4.5);
  for (let p = 100; p >= 0; p -= 5) {
    const c = p === 100 ? b : mixOklab(b, ink, p);
    if (drzi(c)) return c;
  }
  return ink;
}

/** Brand barva jako VÝPLŇ tlačítka → { bg, fg } s ≥ 4,5 : 1.
 *  Bílé písmo, když stačí; jinak tmavé (#14110c); a když nestačí ani jedno
 *  (#c4673a), výplň se ztmaví, aby bílá prošla. */
export function brandVypln(brand) {
  if (!jeHex(brand)) return { bg: brand || "var(--accent)", fg: "var(--on-accent)" };
  const b = brand.trim();
  if (kontrastHex(b, "#ffffff") >= 4.5) return { bg: b, fg: "#fff" };
  if (kontrastHex(b, "#14110c") >= 4.5) return { bg: b, fg: "#14110c" };
  for (let p = 95; p >= 40; p -= 5) {
    const bg = mixOklab(b, "#000000", p);
    if (kontrastHex(bg, "#ffffff") >= 4.5) return { bg, fg: "#fff" };
  }
  return { bg: "#14110c", fg: "#fff" };
}

/** Průhledná varianta barvy. Přípona alfy za `var(--…)` („var(--accent)1f“) je
 *  neplatné CSS — prohlížeč ji mlčky zahodí a podklad/rámeček zmizí. */
export function pruhledna(c, alfaHex) {
  const a = parseInt(alfaHex, 16);
  if (jeHex(c) && c.trim().length === 7) return c.trim() + alfaHex;
  return `color-mix(in srgb, ${c} ${Math.round((a / 255) * 100)}%, transparent)`;
}

/** Brandovaná hlavička dokumentu — logo/jméno poradce vlevo, titul + datum vpravo, barevný pruh.
 *  locale (např. "sk-SK") jen mění formát automatického data; default "cs-CZ" → nic se nerozbije. */
export function printBrandHeader({ title = "", advisor, dateStr, locale = "cs-CZ" } = {}) {
  const a = advisor || {};   // profile může být null (nepřihlášen / fetch fail) — nikdy nespadnout
  const bc = brandColorOf(a);
  const name = esc(a.name);
  const company = esc(a.company_name);
  const phone = esc(a.phone);
  const email = esc(a.public_email);      // jen veřejný e-mail, ne přihlašovací
  const logo = a.company_logo_base64 || "";
  const date = esc(dateStr || new Date().toLocaleDateString(locale));
  const contact = [phone, email].filter(Boolean).join(" · ");
  const left = logo
    ? `<img src="${logo}" style="max-height:42px;max-width:170px;object-fit:contain" alt="logo" />`
    : `<div style="font-size:22px;font-weight:800;color:${bc};letter-spacing:-0.4px">${company || name || "Beplan"}</div>`;
  return `<div style="display:flex;justify-content:space-between;align-items:flex-end;gap:20px;border-bottom:3px solid ${bc};padding-bottom:12px;margin-bottom:18px">
    <div style="display:flex;align-items:center;gap:13px;min-width:0">
      ${left}
      <div style="min-width:0">
        <div style="font-weight:700;font-size:14px;color:#1a1a1a">${name}</div>
        ${contact ? `<div style="font-size:11px;color:#888">${contact}</div>` : ""}
      </div>
    </div>
    <div style="text-align:right;flex-shrink:0">
      <div style="font-size:9px;letter-spacing:1.8px;color:#999;text-transform:uppercase">${esc(title)}</div>
      <div style="font-size:13px;font-weight:600;color:#1a1a1a;margin-top:2px">${date}</div>
    </div>
  </div>`;
}

/** Brandovaná pata — „Připravil <poradce>" + kontakt.
 *  lang="sk" přepne slovo na „Pripravil"; labels.prepared případně přebije. Default cs → nic se nerozbije. */
export function printBrandFooter({ advisor, lang = "cs", labels } = {}) {
  const a = advisor || {};
  const bc = brandColorOf(a);
  const name = esc(a.name || (TLj("poradce", lang)));
  const phone = esc(a.phone);
  const prepared = esc((labels && labels.prepared) || (TLj("Připravil", lang)));
  return `<div style="margin-top:20px;padding-top:10px;border-top:1px solid #eee;font-size:10px;color:#999;text-align:right">
    ${prepared} <b style="color:${bc}">${name}</b>${phone ? " · " + phone : ""}
  </div>`;
}
